import { ConnectKitButton } from "connectkit";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import {
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  IconButton,
  Paper,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAccount } from "wagmi";

import { Access } from "./Access";

const THEME_STORAGE_KEY = "dd-access-theme";

type ThemeMode = "dark" | "light";

console.log("init dd version 2.0.0");

function App() {
  const { isConnected } = useAccount();
  const [themeMode, setThemeMode] = useState<ThemeMode>("dark");

  useEffect(() => {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === "dark" || storedTheme === "light") {
      setThemeMode(storedTheme);
    }
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode,
          primary: {
            main: "#facc15",
            contrastText: "#111111",
          },
          secondary: {
            main: "#2a2a2a",
          },
          background:
            themeMode === "dark"
              ? {
                  default: "#050505",
                  paper: "#121212",
                }
              : {
                  default: "#f7f7f7",
                  paper: "#ffffff",
                },
          text:
            themeMode === "dark"
              ? {
                  primary: "#f5f5f5",
                  secondary: "#b8b8b8",
                }
              : {
                  primary: "#111111",
                  secondary: "#4f4f4f",
                },
          divider: themeMode === "dark" ? "#2b2b2b" : "#d9d9d9",
        },
        shape: {
          borderRadius: 4,
        },
        typography: {
          fontFamily: "Roboto, Segoe UI, sans-serif",
        },
      }),
    [themeMode],
  );

  const toggleTheme = () => {
    setThemeMode((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
      return next;
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", color: "text.primary" }}>
        <AppBar
          position="sticky"
          color="transparent"
          elevation={0}
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            backdropFilter: "blur(8px)",
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ minHeight: 72 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1 }}>
                <Box
                  component="img"
                  src="/duckdao.png"
                  alt="DuckDAO logo"
                  sx={{ width: 132, height: 40, objectFit: "contain", borderRadius: 1 }}
                />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    DuckDAO Access
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Membership Portal
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => window.open("https://duckdao.io/")}
                  sx={{ minWidth: 0, px: 1.25 }}
                >
                  Visit DuckDAO
                </Button>
                <IconButton
                  onClick={toggleTheme}
                  sx={{
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  aria-label={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {themeMode === "dark" ? <LightModeRoundedIcon fontSize="small" /> : <DarkModeRoundedIcon fontSize="small" />}
                </IconButton>
                <ConnectKitButton />
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
          {isConnected ? (
            <Access />
          ) : (
            <Paper
              elevation={4}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                maxWidth: 860,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1.5 }}>
                Connect Wallet to Continue
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Stake DD for DuckDAO Access (DDA), check your current tier, and manage unstaking
                with live penalty estimates.
              </Typography>
            </Paper>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
